<template>
  <section id="services" class="services bg-decoration">
    <div class="container">
      <SectionHeader
        subtitle-key="services.subtitle"
        title-key="services.title"
        description-key="services.description"
      />
      <div class="services-grid">
        <div class="service-card glass glass-glow" v-for="(service, index) in services" :key="index">
          <div class="service-icon glass-sm">
            <span v-html="icons[service.key]"></span>
          </div>
          <h4 class="service-title">{{ $t(`services.items.${service.key}.title`) }}</h4>
          <p class="service-description">{{ $t(`services.items.${service.key}.description`) }}</p>
          <ul class="service-list">
            <li v-for="(item, idx) in $tm(`services.items.${service.key}.list`)" :key="idx">{{ item }}</li>
          </ul>
        </div>
      </div>
    </div>
  </section>
</template>

<script setup>
import { ref } from 'vue'
import SectionHeader from './common/SectionHeader.vue'
import { iconMap } from '@/assets/icons'

const icons = iconMap

const services = ref([
  { key: 'expert' },
  { key: 'data' },
  { key: 'roadshow' },
  { key: 'siteVisit' },
  { key: 'research' },
  { key: 'userResearch' }
])
</script>

<style scoped>
.services {
  padding: 120px 0;
  background: linear-gradient(135deg, var(--bg-light) 0%, color-mix(in srgb, var(--secondary-color) 5%, var(--bg-light)) 50%, color-mix(in srgb, var(--primary-color) 3%, var(--bg-light)) 100%);
  position: relative;
}

.services::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background:
    radial-gradient(circle at 80% 20%, rgba(52, 152, 255, 0.1) 0%, transparent 50%),
    radial-gradient(circle at 20% 80%, rgba(15, 39, 64, 0.06) 0%, transparent 50%);
  pointer-events: none;
}

@media (max-width: 1366px) {
  .services {
    padding: 100px 0;
  }
}

@media (max-width: 1024px) {
  .services {
    padding: 80px 0;
  }
}

@media (max-width: 768px) {
  .services {
    padding: 60px 0;
  }
}

.services-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(350px, 1fr));
  gap: 28px;
  position: relative;
  z-index: 1;
}

@media (max-width: 1366px) {
  .services-grid {
    grid-template-columns: repeat(auto-fit, minmax(320px, 1fr));
    gap: 24px;
  }
}

@media (max-width: 1024px) {
  .services-grid {
    grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
    gap: 20px;
  }
}

@media (max-width: 768px) {
  .services-grid {
    grid-template-columns: 1fr;
    gap: 20px;
  }
}

.service-card {
  padding: var(--spacing-md);
  border-radius: var(--rounded-lg); /* 设计系统大圆角 */
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;
  overflow: hidden;
}

.service-card::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 3px;
  background: linear-gradient(90deg, var(--secondary-color) 0%, color-mix(in srgb, var(--secondary-color) 80%, var(--white)) 50%, var(--secondary-color) 100%);
  background-size: 200% 100%;
  opacity: 0;
  transition: opacity 0.4s ease;
  animation: shimmerBorder 3s ease-in-out infinite;
}

@keyframes shimmerBorder {
  0%, 100% { background-position: 0% 50%; }
  50% { background-position: 100% 50%; }
}

.service-card:hover::before {
  opacity: 1;
}

.service-card:hover {
  transform: translateY(-8px);
}

@media (max-width: 1366px) {
  .service-card {
    padding: 40px 32px;
  }
}

@media (max-width: 1024px) {
  .service-card {
    padding: 35px 28px;
  }
}

@media (max-width: 768px) {
  .service-card {
    padding: 30px 24px;
  }
}

@media (max-width: 375px) {
  .service-card {
    padding: 24px 20px;
  }
}

.service-icon {
  width: 72px;
  height: 72px;
  background: linear-gradient(135deg, color-mix(in srgb, var(--primary-blue) 15%, transparent) 0%, color-mix(in srgb, var(--deep-navy) 8%, transparent) 100%);
  border-radius: var(--rounded-md); /* 设计系统中等圆角 */
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: var(--spacing-sm);
  color: var(--primary-blue); /* 设计系统标准命名 */
  box-shadow: 0 4px 15px color-mix(in srgb, var(--primary-blue) 10%, transparent);
}

@media (max-width: 1366px) {
  .service-icon {
    width: 68px;
    height: 68px;
    margin-bottom: 22px;
  }
}

@media (max-width: 1024px) {
  .service-icon {
    width: 64px;
    height: 64px;
    margin-bottom: 20px;
  }
}

@media (max-width: 768px) {
  .service-icon {
    width: 60px;
    height: 60px;
    margin-bottom: 18px;
  }
}

@media (max-width: 375px) {
  .service-icon {
    width: 56px;
    height: 56px;
    margin-bottom: 16px;
  }
}

.service-icon :deep(svg) {
  width: 40px;
  height: 40px;
}

@media (max-width: 1366px) {
  .service-icon :deep(svg) {
    width: 38px;
    height: 38px;
  }
}

@media (max-width: 1024px) {
  .service-icon :deep(svg) {
    width: 36px;
    height: 36px;
  }
}

@media (max-width: 768px) {
  .service-icon :deep(svg) {
    width: 32px;
    height: 32px;
  }
}

@media (max-width: 375px) {
  .service-icon :deep(svg) {
    width: 30px;
    height: 30px;
  }
}

.service-title {
  font-size: 1.4rem;
  color: var(--deep-navy);
  margin-bottom: var(--spacing-xs);
  font-weight: 700;
  line-height: 1.3;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
}

@media (max-width: 1366px) {
  .service-title {
    font-size: 1.3rem;
    margin-bottom: 12px;
  }
}

@media (max-width: 1024px) {
  .service-title {
    font-size: 1.2rem;
  }
}

@media (max-width: 768px) {
  .service-title {
    font-size: 1.15rem;
    margin-bottom: 10px;
  }
}

@media (max-width: 375px) {
  .service-title {
    font-size: 1.1rem;
  }
}

.service-description {
  color: var(--text-light);
  margin-bottom: var(--spacing-sm);
  line-height: 1.8;
  font-size: clamp(1rem, 1.5vw, 1.1rem); /* 设计系统body级字体 */
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
}

@media (max-width: 1366px) {
  .service-description {
    font-size: 0.95rem;
    margin-bottom: 18px;
  }
}

@media (max-width: 1024px) {
  .service-description {
    font-size: 0.92rem;
    margin-bottom: 16px;
  }
}

@media (max-width: 768px) {
  .service-description {
    font-size: 0.9rem;
    margin-bottom: 14px;
  }
}

@media (max-width: 375px) {
  .service-description {
    font-size: 0.88rem;
  }
}

.service-list {
  padding-top: var(--spacing-sm);
  border-top: 1px solid color-mix(in srgb, var(--primary-blue) 15%, transparent);
}

@media (max-width: 768px) {
  .service-list {
    padding-top: 16px;
  }
}

.service-list li {
  padding: 8px 0;
  color: var(--text-light);
  font-size: 0.95rem;
  position: relative;
  padding-left: 22px;
  line-height: 1.6;
}

@media (max-width: 1024px) {
  .service-list li {
    font-size: 0.92rem;
    padding: 7px 0;
    padding-left: 20px;
  }
}

@media (max-width: 768px) {
  .service-list li {
    font-size: 0.9rem;
    padding: 6px 0;
    padding-left: 18px;
  }
}

.service-list li::before {
  content: '';
  position: absolute;
  left: 0;
  top: 15px;
  width: 8px;
  height: 8px;
  background: linear-gradient(135deg, var(--primary-blue) 0%, color-mix(in srgb, var(--primary-blue) 80%, var(--white)) 100%);
  border-radius: 50%;
  box-shadow: 0 0 0 4px color-mix(in srgb, var(--primary-blue) 15%, transparent);
}

@media (max-width: 1024px) {
  .service-list li::before {
    top: 14px;
    width: 7px;
    height: 7px;
  }
}

@media (max-width: 768px) {
  .service-list li::before {
    top: 13px;
    width: 6px;
    height: 6px;
  }
}
</style>
